import api from './api';

export type LiveStreamStatus =
  | 'created'
  | 'provisioning'
  | 'ready'
  | 'starting'
  | 'live'
  | 'stopping'
  | 'stopped'
  | 'error';

export type StreamingMode = 'rtmp' | 'browser';

export interface LiveStream {
  streamId: string;
  type: 'live';
  streamingMode: StreamingMode;

  // Stream information
  title: string;
  description: string;
  thumbnailUrl?: string;
  category?: string;
  tags?: string[];

  // AWS Resources
  mediaLiveChannelId?: string;
  mediaLiveChannelArn?: string;
  mediaLiveInputId?: string;
  mediaLiveInputArn?: string;
  mediaPackageChannelId?: string;
  mediaPackageEndpointId?: string;

  // Stream configuration (RTMP mode)
  streamKey?: string;
  rtmpUrl?: string;
  rtmpBackupUrl?: string;

  // Playback URLs (both modes)
  hlsPlaybackUrl?: string;
  dashPlaybackUrl?: string;

  // Status and metrics
  status: LiveStreamStatus;
  statusMessage?: string;
  viewerCount: number;
  peakViewerCount: number;
  totalViews: number;

  // User information
  userId: string;
  userName?: string;

  // Timestamps
  createdAt: number;
  updatedAt: number;
  startedAt?: number;
  endedAt?: number;
  scheduledStartTime?: number;

  // Settings
  isRecordingEnabled: boolean;
  recordingUrl?: string;
  isPublic: boolean;
  allowedViewers?: string[];

  // Moderation
  isFeatured: boolean;
  isArchived: boolean;
}

export interface CreateLiveStreamInput {
  title: string;
  description: string;
  streamingMode?: StreamingMode;
  thumbnailUrl?: string;
  category?: string;
  tags?: string[];
  scheduledStartTime?: number;
  isRecordingEnabled?: boolean;
  isPublic?: boolean;
}

export interface UpdateLiveStreamInput {
  title?: string;
  description?: string;
  thumbnailUrl?: string;
  category?: string;
  tags?: string[];
  scheduledStartTime?: number;
  isPublic?: boolean;
}

export interface StreamCredentials {
  rtmpUrl: string;
  rtmpBackupUrl?: string;
  streamKey: string;
}

export const liveStreamService = {
  async getAllLiveStreams(): Promise<LiveStream[]> {
    return api.get<LiveStream[]>('/api/live-streams');
  },

  async getLiveStreamById(streamId: string): Promise<LiveStream> {
    return api.get<LiveStream>(`/api/live-streams/${streamId}`);
  },

  async createLiveStream(input: CreateLiveStreamInput): Promise<LiveStream> {
    return api.post<LiveStream>('/api/live-streams', input);
  },

  async updateLiveStream(streamId: string, input: UpdateLiveStreamInput): Promise<LiveStream> {
    return api.put<LiveStream>(`/api/live-streams/${streamId}`, input);
  },

  async deleteLiveStream(streamId: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/api/live-streams/${streamId}`);
  },

  async startLiveStream(streamId: string): Promise<LiveStream> {
    return api.post<LiveStream>(`/api/live-streams/${streamId}/start`);
  },

  async stopLiveStream(streamId: string): Promise<LiveStream> {
    return api.post<LiveStream>(`/api/live-streams/${streamId}/stop`);
  },

  async getStreamCredentials(streamId: string): Promise<StreamCredentials> {
    return api.get<StreamCredentials>(`/api/live-streams/${streamId}/stream-key`);
  }
};

export default liveStreamService;
